import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eye } from 'lucide-react';
import api, { baixarFicheiro, extrairMensagemErro } from '../services/api';
import PerfilRapidoModal from '../components/PerfilRapidoModal';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';
import { dialog } from '../services/dialog';

type StatusVersaoFactura = 'PENDENTE_VALIDACAO' | 'VALIDADA' | 'INVALIDADA' | 'SUBSTITUIDA';

interface VersaoFactura {
  id: string;
  versao: number;
  nome_original: string;
  mime_type: string;
  status: StatusVersaoFactura;
  motivo_rejeicao: string | null;
  analisado_por?: { id: string; nome: string; email: string } | null;
  data_analise?: string | null;
  data_upload: string;
}

const moeda = (valor: number, codigo = 'KZ') =>
  `${codigo} ${Math.round(Number(valor || 0)).toLocaleString('pt-PT')}`;

const dataCurta = (data?: string) => data ? new Intl.DateTimeFormat('pt-AO').format(new Date(data)) : 'Sem data';

const lerArquivo = (arquivo?: File | null): Promise<any | null> =>
  new Promise((resolve, reject) => {
    if (!arquivo) return resolve(null);
    const reader = new FileReader();
    reader.onload = () => resolve({ nome: arquivo.name, tipo: arquivo.type, conteudo: String(reader.result || '') });
    reader.onerror = reject;
    reader.readAsDataURL(arquivo);
  });

const documentoValido = (arquivo?: File | null) => {
  if (!arquivo) return true;
  const nome = arquivo.name.toLowerCase();
  return ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(arquivo.type) ||
    ['.pdf', '.jpg', '.jpeg', '.png'].some((extensao) => nome.endsWith(extensao));
};

const statusClasse = (estado?: string) => {
  if (['COMPRA_REALIZADA', 'EM_TRANSITO', 'CONCLUIDO'].includes(String(estado))) return 'status-done';
  if (['AGUARDANDO_COMPROVATIVO', 'PROPOSTA_SELECIONADA'].includes(String(estado))) return 'status-warning';
  if (['NAO_SELECIONADA', 'ENCERRADA_EXPIRADA'].includes(String(estado))) return 'status-blocked';
  return '';
};

const badgeStatusFactura = (status: StatusVersaoFactura) => {
  switch (status) {
    case 'PENDENTE_VALIDACAO':
      return <span className="status-pill status-warning">Factura Pendente de Validação</span>;
    case 'VALIDADA':
      return <span className="status-pill status-done">Factura Validada</span>;
    case 'INVALIDADA':
      return <span className="status-pill status-blocked">Factura Invalidada</span>;
    case 'SUBSTITUIDA':
      return <span className="status-pill muted">Factura Substituída</span>;
    default:
      return <span className="status-pill muted">{status}</span>;
  }
};

type AbaVinculosTipo = 'disponiveis' | 'recebidas' | 'enviadas' | 'ativas' | 'historico';

const FornecedorPortal: React.FC = () => {
  const [dashboard, setDashboard] = useState<any>(null);
  const [oportunidades, setOportunidades] = useState<any[]>([]);
  const [propostas, setPropostas] = useState<any[]>([]);
  const [empresasDisponiveis, setEmpresasDisponiveis] = useState<any[]>([]);
  const [vinculos, setVinculos] = useState<any[]>([]);
  const [abaVinculos, setAbaVinculos] = useState<AbaVinculosTipo>('disponiveis');
  const [form, setForm] = useState<Record<string, any>>({});
  const [detalheOportunidade, setDetalheOportunidade] = useState<any | null>(null);
  const [detalheProposta, setDetalheProposta] = useState<any | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [perfilTenantId, setPerfilTenantId] = useState<string | null>(null);
  const [paginacaoOportunidades, setPaginacaoOportunidades] = useState<MetadadosPaginacao>(paginacaoVazia);
  const [paginacaoPropostas, setPaginacaoPropostas] = useState<MetadadosPaginacao>(paginacaoVazia);
  const [paginacaoVinculos, setPaginacaoVinculos] = useState<MetadadosPaginacao>(paginacaoVazia);

  // Histórico de facturas versionadas por proposta
  const [historicoFacturasProp, setHistoricoFacturasProp] = useState<Record<string, VersaoFactura[]>>({});

  const carregarHistoricoProp = useCallback(async (propostaId: string) => {
    try {
      const resp = await api.get(`/fornecedores/propostas/${propostaId}/facturas/historico`);
      setHistoricoFacturasProp((prev) => ({ ...prev, [propostaId]: resp.data.dados || [] }));
    } catch (err) {
      console.error('Erro ao carregar histórico de facturas da proposta:', err);
    }
  }, []);

  const carregarOportunidades = useCallback(async (pagina: number) => {
    setCarregando(true);
    try {
      const resposta = await api.get('/fornecedores/oportunidades', { params: { pagina, limite: paginacaoOportunidades.limite } });
      setOportunidades(resposta.data.dados || []);
      setPaginacaoOportunidades(resposta.data.paginacao || paginacaoVazia);
    } catch (erroLista: any) {
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar oportunidades.'));
    } finally {
      setCarregando(false);
    }
  }, [paginacaoOportunidades.limite]);

  const carregarPropostas = useCallback(async (pagina: number) => {
    setCarregando(true);
    try {
      const resposta = await api.get('/fornecedores/propostas', { params: { pagina, limite: paginacaoPropostas.limite } });
      const listaProps = resposta.data.dados || [];
      setPropostas(listaProps);
      setPaginacaoPropostas(resposta.data.paginacao || paginacaoVazia);

      for (const p of listaProps) {
        if (p.status === 'SELECIONADA') {
          carregarHistoricoProp(p.id);
        }
      }
    } catch (erroLista: any) {
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar propostas.'));
    } finally {
      setCarregando(false);
    }
  }, [paginacaoPropostas.limite, carregarHistoricoProp]);

  const montadoRef = useRef(true);
  const requisicaoIdVinculosRef = useRef(0);
  const ultimaAbaSolicitadaRef = useRef<AbaVinculosTipo>(abaVinculos);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  const carregarVinculos = useCallback(async (pagina = 1, abaEspecifica?: AbaVinculosTipo) => {
    const abaAlvo = abaEspecifica !== undefined ? abaEspecifica : abaVinculos;
    ultimaAbaSolicitadaRef.current = abaAlvo;
    const meuIdRequisicao = ++requisicaoIdVinculosRef.current;
    try {
      setErro(null);
      const [disponiveisResult, vinculosResult] = await Promise.allSettled([
        api.get('/fornecedores/empresas-disponiveis', { params: { pagina, limite: paginacaoVinculos.limite } }),
        api.get('/fornecedores/meus-vinculos', { params: { pagina, limite: paginacaoVinculos.limite, grupo: abaAlvo === 'disponiveis' ? undefined : abaAlvo } })
      ]);

      if (!montadoRef.current || meuIdRequisicao !== requisicaoIdVinculosRef.current || abaAlvo !== ultimaAbaSolicitadaRef.current) return;

      let erros: string[] = [];

      if (disponiveisResult.status === 'fulfilled') {
        if (montadoRef.current && meuIdRequisicao === requisicaoIdVinculosRef.current && abaAlvo === ultimaAbaSolicitadaRef.current) {
          setEmpresasDisponiveis(disponiveisResult.value.data.dados || []);
          if (abaAlvo === 'disponiveis') {
            setPaginacaoVinculos(disponiveisResult.value.data.paginacao || paginacaoVazia);
          }
        }
      } else {
        console.error('Erro ao carregar empresas disponíveis:', disponiveisResult.reason);
        if (montadoRef.current && meuIdRequisicao === requisicaoIdVinculosRef.current && abaAlvo === ultimaAbaSolicitadaRef.current) {
          setEmpresasDisponiveis([]);
          if (abaAlvo === 'disponiveis') {
            setPaginacaoVinculos(paginacaoVazia);
          }
          erros.push(extrairMensagemErro(disponiveisResult.reason, 'Erro ao carregar empresas disponíveis.'));
        }
      }

      if (vinculosResult.status === 'fulfilled') {
        if (montadoRef.current && meuIdRequisicao === requisicaoIdVinculosRef.current && abaAlvo === ultimaAbaSolicitadaRef.current) {
          setVinculos(vinculosResult.value.data.dados || []);
          if (abaAlvo !== 'disponiveis') {
            setPaginacaoVinculos(vinculosResult.value.data.paginacao || paginacaoVazia);
          }
        }
      } else {
        console.error('Erro ao carregar vínculos:', vinculosResult.reason);
        if (montadoRef.current && meuIdRequisicao === requisicaoIdVinculosRef.current && abaAlvo === ultimaAbaSolicitadaRef.current) {
          setVinculos([]);
          if (abaAlvo !== 'disponiveis') {
            setPaginacaoVinculos(paginacaoVazia);
          }
          erros.push(extrairMensagemErro(vinculosResult.reason, 'Erro ao carregar vínculos do fornecedor.'));
        }
      }

      if (erros.length > 0 && montadoRef.current && meuIdRequisicao === requisicaoIdVinculosRef.current && abaAlvo === ultimaAbaSolicitadaRef.current) {
        setErro(erros.join(' '));
      }
    } catch (erroLista: any) {
      if (!montadoRef.current || meuIdRequisicao !== requisicaoIdVinculosRef.current || abaAlvo !== ultimaAbaSolicitadaRef.current) return;
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar vínculos do fornecedor.'));
    }
  }, [abaVinculos, paginacaoVinculos.limite]);

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const [painel, ops, minhas] = await Promise.all([
        api.get('/fornecedores/dashboard'),
        api.get('/fornecedores/oportunidades', { params: { pagina: 1, limite: paginacaoOportunidades.limite } }),
        api.get('/fornecedores/propostas', { params: { pagina: 1, limite: paginacaoPropostas.limite } })
      ]);
      setDashboard(painel.data.dados || null);
      setOportunidades(ops.data.dados || []);
      const listaProps = minhas.data.dados || [];
      setPropostas(listaProps);
      setPaginacaoOportunidades(ops.data.paginacao || paginacaoVazia);
      setPaginacaoPropostas(minhas.data.paginacao || paginacaoVazia);

      for (const p of listaProps) {
        if (p.status === 'SELECIONADA') {
          carregarHistoricoProp(p.id);
        }
      }

      await carregarVinculos();
    } catch (erroLista: any) {
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar portal do fornecedor.'));
    } finally {
      setCarregando(false);
    }
  }, [carregarVinculos, paginacaoOportunidades.limite, paginacaoPropostas.limite, carregarHistoricoProp]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const atualizar = (id: string, campo: string, valor: any) => {
    setForm((atual) => ({ ...atual, [id]: { ...(atual[id] || {}), [campo]: valor } }));
  };

  const abrirOportunidade = async (id: string) => {
    try {
      setErro(null);
      const resposta = await api.get(`/fornecedores/oportunidades/${id}`);
      setDetalheOportunidade(resposta.data.dados);
    } catch (erroDetalhe: any) {
      setErro(extrairMensagemErro(erroDetalhe, 'Erro ao abrir oportunidade.'));
    }
  };

  const abrirProposta = async (id: string) => {
    try {
      setErro(null);
      const resposta = await api.get(`/fornecedores/propostas/${id}`);
      setDetalheProposta(resposta.data.dados);
      carregarHistoricoProp(id);
    } catch (erroDetalhe: any) {
      setErro(extrairMensagemErro(erroDetalhe, 'Erro ao abrir proposta.'));
    }
  };

  const baixarAnexoProposta = async (propostaId: string, tipo: 'documento' | 'comprovativo', nome?: string) => {
    try {
      setErro(null);
      await baixarFicheiro(`/fornecedores/propostas/${propostaId}/${tipo}/download`, nome || 'anexo');
    } catch (erroDownload: any) {
      setErro(extrairMensagemErro(erroDownload, 'Erro ao baixar anexo da proposta.'));
    }
  };

  const baixarVersaoFactura = async (versaoId: string, nome: string) => {
    try {
      setErro(null);
      await baixarFicheiro(`/facturas/versoes/${versaoId}/download`, nome || 'factura.pdf');
    } catch (err: any) {
      setErro(extrairMensagemErro(err, 'Erro ao baixar documento da factura.'));
    }
  };

  const enviar = async (id: string) => {
    const dados = form[id] || {};
    const valorProposto = Number(dados.valor_proposto);
    const tempoEntrega = Number(dados.tempo_entrega_dias);
    const descricaoTecnica = String(dados.descricao_tecnica || '').trim();

    if (!Number.isFinite(valorProposto) || valorProposto <= 0) {
      setErro('Valor proposto deve ser maior que zero.');
      return;
    }
    if (!Number.isInteger(tempoEntrega) || tempoEntrega <= 0) {
      setErro('Tempo de entrega deve ser um número inteiro maior que zero.');
      return;
    }
    if (!descricaoTecnica) {
      setErro('Informe as observações ou especificação técnica da proposta.');
      return;
    }
    if (!documentoValido(dados.documento)) {
      setErro('Anexe um documento válido em PDF, JPG, JPEG ou PNG.');
      return;
    }
    if (!await dialog.confirm('Confirmar envio da proposta? Depois do envio, esta V1 não permite atualizar a proposta.')) return;

    try {
      setErro(null);
      setMensagem(null);
      const documento = await lerArquivo(dados.documento);
      const resposta = await api.post(`/fornecedores/oportunidades/${id}/propostas`, {
        valor_proposto: valorProposto,
        descricao_tecnica: descricaoTecnica,
        tempo_entrega_dias: tempoEntrega,
        documento
      });
      setMensagem(resposta.data.mensagem);
      setDetalheOportunidade(null);
      carregar();
    } catch (erroEnvio: any) {
      setErro(extrairMensagemErro(erroEnvio, 'Erro ao enviar proposta.'));
    }
  };

  const anexarComprovativo = async (propostaId: string) => {
    if (!form[propostaId]?.comprovativo || !documentoValido(form[propostaId]?.comprovativo)) {
      setErro('Anexe um comprovativo válido em PDF, JPG, JPEG ou PNG.');
      return;
    }

    try {
      setErro(null);
      setMensagem(null);
      const comprovativo = await lerArquivo(form[propostaId]?.comprovativo);
      const resposta = await api.post(`/fornecedores/propostas/${propostaId}/comprovativo`, { comprovativo });
      setMensagem(resposta.data.mensagem);
      carregar();
      carregarHistoricoProp(propostaId);
    } catch (erroUpload: any) {
      setErro(extrairMensagemErro(erroUpload, 'Erro ao anexar comprovativo.'));
    }
  };

  const executarAcaoVinculo = async (acao: () => Promise<any>, mensagemErro: string) => {
    try {
      setErro(null);
      setMensagem(null);
      const resposta = await acao();
      setMensagem(resposta.data?.mensagem || 'Vínculo atualizado.');
      await Promise.all([carregarVinculos(), carregar()]);
    } catch (erroAcao: any) {
      setErro(extrairMensagemErro(erroAcao, mensagemErro));
    }
  };

  const solicitarVinculo = (tenantId: string) =>
    executarAcaoVinculo(() => api.post('/fornecedores/vinculos/solicitar', { tenant_id: tenantId }), 'Erro ao solicitar vínculo.');

  const cancelarVinculo = (vinculoId: string) =>
    executarAcaoVinculo(() => api.post(`/fornecedores/vinculos/${vinculoId}/cancelar`), 'Erro ao cancelar solicitação.');

  const aceitarVinculo = (vinculoId: string) =>
    executarAcaoVinculo(() => api.post(`/fornecedores/vinculos/${vinculoId}/aceitar`), 'Erro ao aceitar solicitação.');

  const recusarVinculo = (vinculoId: string) =>
    executarAcaoVinculo(() => api.post(`/fornecedores/vinculos/${vinculoId}/recusar`, { motivo: form[vinculoId]?.motivo }), 'Erro ao recusar solicitação.');

  const desativarVinculo = async (vinculoId: string) => {
    if (!await dialog.confirm('Desativar vínculo com esta empresa? Para voltar a operar será necessária nova solicitação.')) return;
    executarAcaoVinculo(() => api.post(`/fornecedores/vinculos/${vinculoId}/desativar`, { motivo: form[vinculoId]?.motivo }), 'Erro ao desativar vínculo.');
  };

  const indicadores = dashboard?.indicadores || {};
  const vinculosRecebidos = vinculos.filter((item) => item.status === 'PENDENTE' && item.solicitado_por === 'TENANT');
  const vinculosEnviados = vinculos.filter((item) => item.status === 'PENDENTE' && item.solicitado_por === 'FORNECEDOR');
  const vinculosAtivos = vinculos.filter((item) => item.status === 'ATIVO');
  const vinculosHistorico = vinculos.filter((item) => !['PENDENTE', 'ATIVO'].includes(item.status));

  return (
    <div className="workspace-page supplier-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Área externa</span>
          <h1>Portal do fornecedor</h1>
          <p>Oportunidades abertas, propostas enviadas e comprovativos pendentes.</p>
        </div>
      </header>

      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
      {erro && <div className="alerta alerta-erro">{erro}</div>}
      {carregando && <div className="inline-loading">A carregar dados operacionais...</div>}

      <section className="metric-grid supplier-metrics">
        {[
          ['Oportunidades disponíveis', indicadores.oportunidades_disponiveis ?? indicadores.oportunidades_abertas],
          ['Propostas submetidas', indicadores.propostas_submetidas ?? indicadores.propostas_enviadas],
          ['Propostas aprovadas', indicadores.propostas_aprovadas ?? indicadores.propostas_selecionadas],
          ['Propostas rejeitadas', indicadores.propostas_rejeitadas],
          ['Compras adjudicadas', indicadores.compras_adjudicadas ?? indicadores.compras_realizadas],
          ['Contratos activos', indicadores.contratos_activos ?? indicadores.empresas_vinculadas_ativas]
        ].map(([label, valor]) => (
          <article className="metric-card" key={label}>
            <span>{label}</span>
            <strong>{Number(valor || 0)}</strong>
          </article>
        ))}
      </section>

      <section className="operation-grid supplier-layout">
        <div className="supplier-main-column">
          <section className="panel rule-panel">
            <div className="panel-heading">
              <h2>Vínculos com empresas</h2>
              <button className="botao" type="button" onClick={() => carregarVinculos(paginacaoVinculos.pagina)}>Atualizar</button>
            </div>
            <div className="tab-strip">
              {[
                ['disponiveis', 'Empresas disponíveis'],
                ['recebidas', 'Recebidas'],
                ['enviadas', 'Enviadas'],
                ['ativas', 'Vinculadas'],
                ['historico', 'Histórico']
              ].map(([id, label]) => (
                <button key={id} type="button" className={abaVinculos === id ? 'active' : ''} onClick={() => { setAbaVinculos(id as AbaVinculosTipo); carregarVinculos(1, id as AbaVinculosTipo); }}>{label}</button>
              ))}
            </div>
            <div className="stacked-list">
              {abaVinculos === 'disponiveis' && empresasDisponiveis.map((empresa) => (
                <div className="list-row" key={empresa.id}>
                  <span>{empresa.nome_empresa}<small>{empresa.cnpj_nif}</small></span>
                  <button className="botao botao-primario" type="button" onClick={() => solicitarVinculo(empresa.id)}>Solicitar</button>
                </div>
              ))}
              {abaVinculos === 'recebidas' && vinculosRecebidos.map((vinculo) => (
                <div className="list-row" key={vinculo.id}>
                  <span>{vinculo.tenant?.nome_empresa || vinculo.tenant_id}<small>Solicitação recebida</small></span>
                  <div className="inline-actions">
                    <button className="botao botao-primario" type="button" onClick={() => aceitarVinculo(vinculo.id)}>Aceitar</button>
                    <button className="botao botao-secundario" type="button" onClick={() => recusarVinculo(vinculo.id)}>Recusar</button>
                  </div>
                </div>
              ))}
              {abaVinculos === 'enviadas' && vinculosEnviados.map((vinculo) => (
                <div className="list-row" key={vinculo.id}>
                  <span>{vinculo.tenant?.nome_empresa || vinculo.tenant_id}<small>Aguardando resposta</small></span>
                  <button className="botao botao-secundario" type="button" onClick={() => cancelarVinculo(vinculo.id)}>Cancelar</button>
                </div>
              ))}
              {abaVinculos === 'ativas' && vinculosAtivos.map((vinculo) => (
                <div className="list-row" key={vinculo.id}>
                  <span>{vinculo.tenant?.nome_empresa || vinculo.tenant_id}<small>Acesso operacional ativo.</small></span>
                  <strong><span className="status-pill status-done">{vinculo.status}</span></strong>
                  <button className="botao" type="button" onClick={() => setPerfilTenantId(vinculo.tenant_id)}><Eye size={16} aria-hidden="true" />Ver perfil</button>
                  <button className="botao botao-secundario" type="button" onClick={() => desativarVinculo(vinculo.id)}>Desativar</button>
                </div>
              ))}
              {abaVinculos === 'historico' && vinculosHistorico.map((vinculo) => (
                <div className="list-row" key={vinculo.id}>
                  <span>{vinculo.tenant?.nome_empresa || vinculo.tenant_id}<small>{vinculo.motivo_recusa || vinculo.motivo_desativacao || 'Sem motivo registado.'}</small></span>
                  <strong><span className={`status-pill ${statusClasse(vinculo.status)}`}>{vinculo.status}</span></strong>
                </div>
              ))}
              {abaVinculos === 'disponiveis' && empresasDisponiveis.length === 0 && <div className="empty-state">Sem empresas disponíveis.</div>}
              {abaVinculos === 'recebidas' && vinculosRecebidos.length === 0 && <div className="empty-state">Sem solicitações recebidas.</div>}
              {abaVinculos === 'enviadas' && vinculosEnviados.length === 0 && <div className="empty-state">Sem solicitações enviadas.</div>}
              {abaVinculos === 'ativas' && vinculosAtivos.length === 0 && <div className="empty-state">Sem empresas vinculadas.</div>}
              {abaVinculos === 'historico' && vinculosHistorico.length === 0 && <div className="empty-state">Sem histórico de vínculos.</div>}
              <Paginacao paginacao={paginacaoVinculos} carregando={carregando} aoMudarPagina={carregarVinculos} />
            </div>
          </section>

          <section className="panel">
            <div className="panel-heading">
              <h2>Oportunidades</h2>
              <button className="botao" type="button" onClick={carregar}>Atualizar</button>
            </div>
            <div className="card-list compact">
              {oportunidades.map((item) => (
                <article className="supplier-opportunity" key={item.id}>
                  <div className="warehouse-card-header">
                    <span className="status-pill">{item.empresa_compradora}</span>
                    <span className="status-pill muted">{item.numero_requisicao}</span>
                    <span className="status-pill muted">Até {dataCurta(item.data_vencimento)}</span>
                  </div>
                  <h3>{item.titulo}</h3>
                  <dl className="meta-grid">
                    <div><dt>Estado</dt><dd>{item.estado_operacional_label}</dd></div>
                    <div><dt>Itens</dt><dd>{item.quantidade_itens}</dd></div>
                    <div><dt>Cotação</dt><dd>{item.ja_enviou_proposta ? 'Proposta enviada' : 'Aberta'}</dd></div>
                  </dl>
                  <div className="inline-actions">
                    <button className="botao" type="button" onClick={() => setPerfilTenantId(item.tenant_id)}><Eye size={16} aria-hidden="true" />Ver perfil</button>
                    <button className="botao" type="button" onClick={() => abrirOportunidade(item.id)}>Ver detalhes</button>
                  </div>
                  {item.ja_enviou_proposta && (
                    <div className="alerta alerta-info">
                      Proposta registada: {item.minha_proposta?.status || 'ENVIADA'}.
                    </div>
                  )}
                </article>
              ))}
              {!carregando && oportunidades.length === 0 && <div className="empty-state">Nenhuma oportunidade aberta para os seus vínculos ativos.</div>}
              {!carregando && <Paginacao paginacao={paginacaoOportunidades} carregando={carregando} aoMudarPagina={carregarOportunidades} />}
            </div>
          </section>
        </div>

        <div className="supplier-main-column">
          <section className="panel supplier-notifications">
            <div className="panel-heading">
              <h2>Notificações</h2>
            </div>
            <div className="stacked-list">
              {(dashboard?.notificacoes || []).map((notificacao: any) => (
                <div className="list-row" key={notificacao.id}>
                  <span>{notificacao.mensagem}</span>
                  <strong><span className={`status-pill ${statusClasse(notificacao.tipo)}`}>{notificacao.status}</span></strong>
                  <small>{notificacao.empresa_compradora} · {dataCurta(notificacao.created_at || notificacao.createdAt)}</small>
                </div>
              ))}
              {(!dashboard?.notificacoes || dashboard.notificacoes.length === 0) && (
                <div className="empty-state">Sem notificações operacionais recentes.</div>
              )}
            </div>
          </section>

          <section className="panel">
            <div className="panel-heading">
              <h2>Minhas propostas</h2>
            </div>
            <div className="supplier-proposal-list">
              {propostas.map((proposta) => {
                const versoesProp = historicoFacturasProp[proposta.id] || [];
                const ultimaVersao = versoesProp[0]; // mais recente
                const temPendenteOuValidada = versoesProp.some(v => ['PENDENTE_VALIDACAO', 'VALIDADA'].includes(v.status));
                const podeAnexar = proposta.status === 'SELECIONADA' &&
                  ['PROPOSTA_SELECIONADA', 'AGUARDANDO_COMPROVATIVO'].includes(proposta.requisicao?.status) &&
                  !temPendenteOuValidada;

                return (
                  <article className="proposal-cell" key={proposta.id}>
                    <div className="warehouse-card-header">
                      <span className={`status-pill ${statusClasse(proposta.estado_operacional)}`}>{proposta.estado_operacional_label}</span>
                      <span className="status-pill muted">{proposta.requisicao?.numero_requisicao}</span>
                    </div>
                    <strong>{proposta.requisicao?.titulo}</strong>
                    <small>{proposta.requisicao?.tenant?.nome_empresa} · {dataCurta(proposta.data_criacao)}</small>
                    <dl className="meta-grid">
                      <div><dt>Valor</dt><dd>{moeda(proposta.valor_proposto, proposta.moeda)}</dd></div>
                      <div><dt>Prazo</dt><dd>{proposta.tempo_entrega_dias} dias</dd></div>
                    </dl>
                    {proposta.documento_nome ? (
                      <button className="botao" type="button" onClick={() => baixarAnexoProposta(proposta.id, 'documento', proposta.documento_nome)}>
                        Baixar proforma
                      </button>
                    ) : (
                      <small>Sem proforma</small>
                    )}

                    {/* Exibição do Histórico de Facturas / Versões */}
                    {proposta.status === 'SELECIONADA' && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Estado da Factura</span>
                          <button className="botao" type="button" style={{ fontSize: '0.8rem', padding: '0.2rem 0.5rem' }} onClick={() => carregarHistoricoProp(proposta.id)}>Atualizar</button>
                        </div>

                        {versoesProp.length === 0 && (
                          <small style={{ color: '#64748b', display: 'block' }}>Nenhuma factura submetida ainda.</small>
                        )}

                        {versoesProp.map((versao) => (
                          <div key={versao.id} style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span><strong>Versão {versao.versao}</strong> · {versao.nome_original}</span>
                              {badgeStatusFactura(versao.status)}
                            </div>
                            <small style={{ color: '#64748b', display: 'block', marginTop: '0.2rem' }}>Enviada em: {dataCurta(versao.data_upload)}</small>

                            {versao.status === 'INVALIDADA' && versao.motivo_rejeicao && (
                              <div className="alerta alerta-erro" style={{ margin: '0.5rem 0 0 0', padding: '0.5rem', fontSize: '0.85rem' }}>
                                <strong>Factura Invalidada!</strong> Motivo: {versao.motivo_rejeicao}
                                {versao.analisado_por && <small style={{ display: 'block' }}>Por: {versao.analisado_por.nome} em {dataCurta(versao.data_analise || undefined)}</small>}
                              </div>
                            )}

                            <div style={{ marginTop: '0.5rem' }}>
                              <button className="botao" type="button" style={{ fontSize: '0.8rem' }} onClick={() => baixarVersaoFactura(versao.id, versao.nome_original)}>
                                Baixar documento
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {podeAnexar && (
                      <div className="table-upload" style={{ marginTop: '0.75rem' }}>
                        <input accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" type="file" onChange={(e) => atualizar(proposta.id, 'comprovativo', e.target.files?.[0])} />
                        <button className="botao botao-primario" type="button" onClick={() => anexarComprovativo(proposta.id)}>
                          {ultimaVersao?.status === 'INVALIDADA' ? 'Enviar Versão Corrigida' : 'Enviar Fatura / Comprovativo'}
                        </button>
                      </div>
                    )}

                    <div className="inline-actions" style={{ marginTop: '0.5rem' }}>
                      <button className="botao" type="button" onClick={() => setPerfilTenantId(proposta.tenant_id)}><Eye size={16} aria-hidden="true" />Ver perfil</button>
                      <button className="botao" type="button" onClick={() => abrirProposta(proposta.id)}>Ver detalhes</button>
                    </div>
                  </article>
                );
              })}
              {!carregando && propostas.length === 0 && <div className="empty-state">Ainda não há propostas enviadas.</div>}
              {!carregando && <Paginacao paginacao={paginacaoPropostas} carregando={carregando} aoMudarPagina={carregarPropostas} />}
            </div>
          </section>
        </div>
      </section>

      {detalheOportunidade && (
        <div className="modal-backdrop requisition-modal-overlay" onClick={() => setDetalheOportunidade(null)}>
          <div className="panel proposal-modal supplier-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="panel-heading">
              <h2>{detalheOportunidade.numero_requisicao}</h2>
              <button className="botao" type="button" onClick={() => setDetalheOportunidade(null)}>Fechar</button>
            </div>
            <h3>{detalheOportunidade.titulo}</h3>
            <dl className="meta-grid">
              <div><dt>Empresa</dt><dd>{detalheOportunidade.empresa_compradora}</dd></div>
              <div><dt>Prazo limite</dt><dd>{dataCurta(detalheOportunidade.prazo_limite)}</dd></div>
              <div><dt>Estado</dt><dd>{detalheOportunidade.estado_operacional_label}</dd></div>
            </dl>
            <button className="botao" type="button" onClick={() => setPerfilTenantId(detalheOportunidade.tenant_id)}><Eye size={16} aria-hidden="true" />Ver perfil da empresa</button>
            <div className="detail-section">
              <h3>Itens solicitados</h3>
              <div className="supplier-item-list">
                {(detalheOportunidade.itens || []).map((item: any, index: number) => (
                  <div className="scope-item" key={`${item.nome}-${index}`}>
                    <span>
                      <strong>{item.nome}</strong>
                      <small>{item.descricao_tecnica || 'Sem observações adicionais.'}</small>
                    </span>
                    <span className="status-pill muted">{item.quantidade} {item.unidade}</span>
                  </div>
                ))}
              </div>
            </div>
            {detalheOportunidade.aceita_proposta ? (
              <div className="supplier-form">
                <input placeholder="Valor total" type="number" onChange={(e) => atualizar(detalheOportunidade.id, 'valor_proposto', e.target.value)} />
                <input placeholder="Prazo de entrega em dias" type="number" onChange={(e) => atualizar(detalheOportunidade.id, 'tempo_entrega_dias', e.target.value)} />
                <textarea placeholder="Observações comerciais e especificação técnica" onChange={(e) => atualizar(detalheOportunidade.id, 'descricao_tecnica', e.target.value)} />
                <input accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" type="file" onChange={(e) => atualizar(detalheOportunidade.id, 'documento', e.target.files?.[0])} />
                <button className="botao botao-primario" type="button" onClick={() => enviar(detalheOportunidade.id)}>Enviar proposta</button>
              </div>
            ) : (
              <div className="alerta alerta-info">Esta oportunidade não aceita nova proposta neste momento.</div>
            )}
          </div>
        </div>
      )}

      {detalheProposta && (
        <div className="modal-backdrop requisition-modal-overlay" onClick={() => setDetalheProposta(null)}>
          <div className="panel proposal-modal supplier-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="panel-heading">
              <h2>{detalheProposta.requisicao?.numero_requisicao}</h2>
              <button className="botao" type="button" onClick={() => setDetalheProposta(null)}>Fechar</button>
            </div>
            <h3>{detalheProposta.requisicao?.titulo}</h3>
            <dl className="meta-grid">
              <div><dt>Empresa</dt><dd>{detalheProposta.requisicao?.tenant?.nome_empresa}</dd></div>
              <div><dt>Valor submetido</dt><dd>{moeda(detalheProposta.valor_proposto, detalheProposta.moeda)}</dd></div>
              <div><dt>Prazo</dt><dd>{detalheProposta.tempo_entrega_dias} dias</dd></div>
              <div><dt>Resultado</dt><dd>{detalheProposta.resultado_selecao}</dd></div>
            </dl>
            <button className="botao" type="button" onClick={() => setPerfilTenantId(detalheProposta.tenant_id)}><Eye size={16} aria-hidden="true" />Ver perfil da empresa</button>
            <p className="justification-text">{detalheProposta.descricao_tecnica}</p>
            <div className="detail-section">
              <h3>Anexos</h3>
              <div className="stacked-list">
                <div className="list-row">
                  <span>Proforma</span>
                  <strong>{detalheProposta.anexos?.proposta?.nome || 'Sem proforma'}</strong>
                  {detalheProposta.anexos?.proposta ? (
                    <button className="botao" type="button" onClick={() => baixarAnexoProposta(detalheProposta.id, 'documento', detalheProposta.anexos.proposta.nome)}>
                      Baixar proforma
                    </button>
                  ) : null}
                </div>
                <div className="list-row">
                  <span>Fatura / Comprovativo</span>
                  <strong>{detalheProposta.anexos?.comprovativo?.nome || 'Ainda não enviado'}</strong>
                  {detalheProposta.anexos?.comprovativo && (
                    <>
                      <small>{detalheProposta.anexos.comprovativo.validado_em ? 'Validado pelo comprador.' : 'Pendente de validação do comprador.'}</small>
                      <button className="botao" type="button" onClick={() => baixarAnexoProposta(detalheProposta.id, 'comprovativo', detalheProposta.anexos.comprovativo.nome)}>
                        Baixar fatura / comprovativo de pagamento
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="detail-section">
              <h3>Histórico</h3>
              <div className="history-list">
                {(detalheProposta.historico || []).map((evento: any) => (
                  <div className="history-item" key={`${evento.evento}-${evento.data}`}>
                    <time>{dataCurta(evento.data)}</time>
                    <p>{evento.descricao}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <PerfilRapidoModal
        tipo="tenant"
        id={perfilTenantId}
        aberto={Boolean(perfilTenantId)}
        onClose={() => setPerfilTenantId(null)}
      />
    </div>
  );
};

export default FornecedorPortal;
